import { useState, useEffect } from "react";
import { useAuth } from "@/context/auth-context";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navTheme, setNavTheme] = useState<"dark" | "light">("dark");
  const { user, isAdmin, signOut } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Intersection Observer: detect which section the navbar overlaps
  useEffect(() => {
    const sections = document.querySelectorAll("[data-nav-theme]");
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry that's intersecting and closest to the top
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const theme = (entry.target as HTMLElement).dataset.navTheme as "dark" | "light";
            if (theme) setNavTheme(theme);
          }
        }
      },
      {
        // Only observe the narrow strip where the navbar sits
        rootMargin: "0px 0px -95% 0px",
        threshold: 0,
      }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const isDark = navTheme === "dark";
  const textColor = isDark ? "text-white" : "text-foreground";
  const textMuted = isDark ? "text-white/80" : "text-foreground/70";
  const textFaint = isDark ? "text-white/60" : "text-foreground/50";
  const iconColor = isDark ? "text-white" : "text-foreground";

  const navBg = scrolled
    ? isDark
      ? "bg-ink/90 backdrop-blur-sm"
      : "bg-white/90 backdrop-blur-sm shadow-sm"
    : "";

  const navGradient = scrolled
    ? undefined
    : isDark
      ? "linear-gradient(to bottom, rgba(44,37,32,0.8), transparent)"
      : "linear-gradient(to bottom, rgba(255,255,255,0.8), transparent)";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 px-4 sm:px-[4vw] py-4 sm:py-6 flex items-center justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] transition-all duration-500 ${navBg}`}
        style={{ background: navGradient }}
      >
        {/* Desktop left nav */}
        <div className="hidden lg:flex gap-8">
          <a href="/#details" className={`font-sans text-xs uppercase tracking-[0.15em] font-medium ${textMuted} hover:${isDark ? 'text-white' : 'text-foreground'} transition-colors`}>
            Details
          </a>
          <a href="/gallery" className={`font-sans text-xs uppercase tracking-[0.15em] font-medium ${textMuted} hover:${isDark ? 'text-white' : 'text-foreground'} transition-colors`}>
            Gallery
          </a>
          {isAdmin && (
            <a href="/admin" className="font-sans text-xs uppercase tracking-[0.15em] font-medium text-gold hover:opacity-80 transition-colors">
              Admin
            </a>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden p-1 ${iconColor} transition-colors duration-500`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Center brand */}
        <a href="/" className={`font-display text-lg sm:text-xl uppercase tracking-[0.1em] ${textColor} text-center transition-colors duration-500`}>
          For The City
          <span className={`block text-[0.55rem] sm:text-[0.6rem] tracking-[0.2em] font-sans font-light ${isDark ? 'opacity-70' : 'opacity-60'}`}>
            presents The All White Experience
          </span>
        </a>

        {/* Right actions */}
        <div className="flex items-center justify-end gap-2 sm:gap-4">
          <span className={`hidden md:inline font-sans font-semibold text-sm ${textColor} transition-colors duration-500`}>
            +1 (937) 266-5786
          </span>

          {user ? (
            <div className="hidden sm:flex items-center gap-3">
              <a
                href="/my-tickets"
                className={`font-sans text-xs uppercase tracking-[0.1em] font-medium ${textMuted} hover:opacity-100 transition-colors duration-500`}
              >
                My Tickets
              </a>
              <button
                onClick={() => signOut()}
                className={`font-sans text-xs uppercase tracking-[0.1em] font-medium ${textFaint} hover:opacity-100 transition-colors duration-500`}
              >
                Sign Out
              </button>
            </div>
          ) : (
            <a
              href="/login"
              className={`hidden sm:inline font-sans text-xs uppercase tracking-[0.1em] font-medium ${textMuted} hover:opacity-100 transition-colors duration-500`}
            >
              Sign In
            </a>
          )}

          <a
            href="/#tickets"
            className="bg-gold text-ink px-3 py-2 sm:px-5 sm:py-3 font-sans font-semibold text-[10px] sm:text-xs uppercase tracking-[0.1em] rounded-sm hover:-translate-y-0.5 transition-transform active:scale-[0.97]"
          >
            Buy Tickets
          </a>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-white/90 backdrop-blur-sm lg:hidden flex flex-col items-center justify-center gap-8 pt-20">
          <a
            href="/#details"
            onClick={() => setMobileOpen(false)}
            className="font-heading text-lg uppercase tracking-[0.2em] text-[#0D1B2A] hover:text-gold transition-colors"
          >
            Details
          </a>
          <a
            href="/gallery"
            onClick={() => setMobileOpen(false)}
            className="font-heading text-lg uppercase tracking-[0.2em] text-[#0D1B2A] hover:text-gold transition-colors"
          >
            Gallery
          </a>
          <a
            href="/#tickets"
            onClick={() => setMobileOpen(false)}
            className="font-heading text-lg uppercase tracking-[0.2em] text-[#0D1B2A] hover:text-gold transition-colors"
          >
            Tickets
          </a>
          {isAdmin && (
            <a
              href="/admin"
              className="font-heading text-lg uppercase tracking-[0.2em] text-gold hover:text-[#0D1B2A] transition-colors"
            >
              Admin
            </a>
          )}
          <div className="border-t border-[#0D1B2A]/10 pt-8 flex flex-col items-center gap-4">
            {user ? (
              <>
                <a href="/my-tickets" className="font-sans text-sm text-[#0D1B2A]/70 hover:text-[#0D1B2A] transition-colors">
                  My Tickets
                </a>
                <button
                  onClick={() => { signOut(); setMobileOpen(false); }}
                  className="font-sans text-sm text-[#0D1B2A]/50 hover:text-[#0D1B2A] transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <a href="/login" className="font-sans text-sm text-[#0D1B2A]/70 hover:text-[#0D1B2A] transition-colors">
                Sign In
              </a>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
